import { SendEmailMutation } from "#src/api/Email.ts";
import LinkButton from "#src/components/buttons/LinkButton.tsx";
import { useForm } from "#src/components/forms/Form.tsx";
import { InputItem } from "#src/components/inputs/InputItem.tsx";
import { InputText } from "#src/components/inputs/InputText.tsx";
import InputTextarea from "#src/components/inputs/InputTextarea.tsx";
import { ContactOrganizationModal } from "#src/components/modals/ContactOrganizationModal.tsx";
import Modal from "#src/components/modals/Modal.tsx";
import { apiClient } from "#src/stores/Api.tsx";
import { useStore } from "#src/stores/Store.tsx";
import message from "#src/utils/message.tsx";
import { modals, useModal } from "#src/utils/modals.ts";
import { EmailType, sendEmailVariables } from "#types/code-generator.ts";
import { BorderButton } from "../buttons/BorderButton";

interface Props {
  title?: string;
  sendToSupport?: boolean;
  deleteAccount?: boolean;
  testEmail?: boolean;
  sendToOneEleven?: boolean;
}

export const ContactSupportModal = modals.create((props: Props) => {
  const store = useStore();
  const modal = useModal();
  const form = useForm();

  async function send() {
    void form.validateFields(async (err, _values) => {
      if (err) {
        console.log("errors", err);
        return;
      }

      let body = form.getFieldValue("message");

      if (props.testEmail) {
        body = `This is a test email from ${store.organization.title}.`;
      }

      let replyTo = form.getFieldValue("email");
      let name = replyTo;
      let html = `
        <p>${replyTo} has a question/message:</p>
        <p>${body}</p>
      `;
      let type = undefined;

      if (store.user) {
        replyTo = store.user.email;
        name = `${store.user.firstName} ${store.user.lastName}`;
        html = `
          <p><a href="https://${store.organization.subdomain}.captyn.com/admin/users/${store.user.id}">${name}</a> (${replyTo}) has a question/message:</p>
          <p>${body}</p>
        `;
      }

      let sendToOrganization = true;
      let sendToSupport = props.sendToSupport;
      const additionalEmails = [];

      if (sendToSupport) {
        sendToOrganization = false;
        additionalEmails.push("support@captyn.com");
      }

      if (props.sendToOneEleven) {
        sendToOrganization = false;
        additionalEmails.push("support@oneelevendigital.com");
      }

      let subject = `Message from ${name}`;

      if (props.deleteAccount) {
        subject = `Delete Account Request from ${name}`;
        html = `
          <p>${replyTo} has a delete account request:</p>
          <p>${body}</p>
        `;
      }

      if (props.testEmail) {
        subject = `Test Email from ${store.organization.title}`;
        html = `
          <p>${body}</p>
        `;
        sendToOrganization = false;
        additionalEmails.push(form.getFieldValue("email"));
        type = EmailType.Code;
        replyTo = undefined;
      }

      if (props.sendToOneEleven) {
        subject = `Captyn Website Support Request from ${name}`;
        html = `
          <p>${body}</p>
        `;
        type = EmailType.Code;
      }

      const variables: sendEmailVariables = {
        data: {
          users: [],
          additionalEmails,
          sendToOrganization,
          subject,
          replyTo,
          organizationId: store.organization.id,
          html,
          type,
        },
      };

      await apiClient(SendEmailMutation, {
        variables,
      });

      void modal.hide();

      message.success("Email sent");
    });
  }

  function showContactOrganizationModal() {
    modal.hide();
    modals.show(ContactOrganizationModal);
  }

  let title = "Send Captyn Support a Message";
  let intro = <></>;
  let label = "Question/Message";

  if (props.title) {
    title = props.title;
  }

  if (props.sendToOneEleven) {
    intro = (
      <p>
        Send a message about your website. This will go to{" "}
        <strong>OneEleven</strong>, Captyn’s website support partner.
      </p>
    );
    label = "What do you need help with?";
  }

  if (props.sendToSupport) {
    intro = (
      <>
        <p>
          For technical questions about this app, please send a message below.
        </p>

        <p>
          If you’re trying to contact {store.organization.title}, please{" "}
          <LinkButton onClick={showContactOrganizationModal}>
            click here
          </LinkButton>
          .
        </p>
      </>
    );
  }

  return (
    <Modal
      visible={modal.visible}
      handleCancel={modal.hide}
      afterClose={modal.remove}
      title={title}
      footer={
        <>
          <BorderButton type="submit" onClick={send}>
            Send
          </BorderButton>
          <BorderButton onClick={modal.hide}>Cancel</BorderButton>
        </>
      }
    >
      {intro}

      {!store.user && (
        <InputItem
          label="Email"
          name="email"
          rules={[{ required: true, message: "Please enter an email" }]}
          form={form}
        >
          <InputText placeholder="Your email address" type="email" />
        </InputItem>
      )}

      {props.testEmail && (
        <InputItem
          label="Email"
          name="email"
          rules={[{ required: true, message: "Please enter an email" }]}
          form={form}
        >
          <InputText placeholder="Test recipient" type="email" />
        </InputItem>
      )}

      {props.deleteAccount && (
        <p>We will confirm your account deletion within 5 business days.</p>
      )}

      {!props.testEmail && (
        <InputItem
          label={label}
          name="message"
          rules={[{ required: true, message: "Please enter a message" }]}
          form={form}
        >
          <InputTextarea placeholder="Your message" />
        </InputItem>
      )}

      {props.sendToOneEleven && (
        <div className="text-empty mb-4 text-sm">
          Larger updates or requests outside your website scope may require
          additional billing.
        </div>
      )}
    </Modal>
  );
});
